import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true, cannot be disabled
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookie-consent');
        if (!consent) {
            // Show banner after a short delay
            const timer = setTimeout(() => setShowBanner(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const acceptAll = () => {
        const allAccepted = { necessary: true, analytics: true, marketing: true };
        localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
        localStorage.setItem('cookie-consent-date', new Date().toISOString());
        setShowBanner(false);
    };

    const acceptSelected = () => {
        localStorage.setItem('cookie-consent', JSON.stringify(preferences));
        localStorage.setItem('cookie-consent-date', new Date().toISOString());
        setShowBanner(false);
    };

    const rejectAll = () => {
        const onlyNecessary = { necessary: true, analytics: false, marketing: false };
        localStorage.setItem('cookie-consent', JSON.stringify(onlyNecessary));
        localStorage.setItem('cookie-consent-date', new Date().toISOString());
        setShowBanner(false);
    };

    if (!showBanner) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-2xl max-h-[70vh] overflow-y-auto">
            <div className="max-w-6xl mx-auto">
                {!showSettings ? (
                    // Main banner
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
                        <div className="flex items-start gap-3 flex-1">
                            <Cookie className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-slate-800 dark:text-white mb-1">
                                    🍪 Използваме бисквитки
                                </h3>
                                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 line-clamp-3 sm:line-clamp-none">
                                    Този сайт използва бисквитки за подобряване на вашето изживяване. 
                                    Някои бисквитки са задължителни за работата на сайта, докато други ни помагат 
                                    да анализираме трафика и да персонализираме съдържанието.{' '}
                                    <Link to="/privacy" className="text-amber-600 hover:underline">
                                        Научете повече
                                    </Link>
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 w-full md:w-auto">
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setShowSettings(true)}
                                className="flex items-center gap-1"
                            >
                                <Settings className="w-4 h-4" />
                                Настройки
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={rejectAll}
                            >
                                Само необходими
                            </Button>
                            <Button 
                                size="sm"
                                onClick={acceptAll}
                                className="bg-amber-500 hover:bg-amber-600 text-white"
                            >
                                Приемам всички
                            </Button>
                        </div>
                    </div>
                ) : (
                    // Settings panel
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                                <Settings className="w-5 h-5" />
                                Настройки за бисквитки
                            </h3>
                            <button onClick={() => setShowSettings(false)} className="text-slate-500 hover:text-slate-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="space-y-3 mb-4">
                            {/* Necessary cookies */}
                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                <div>
                                    <p className="font-medium text-slate-800 dark:text-white">Необходими бисквитки</p>
                                    <p className="text-xs text-slate-500">Задължителни за работата на сайта</p>
                                </div>
                                <div className="bg-green-500 text-white text-xs px-2 py-1 rounded">Винаги активни</div>
                            </div>
                            
                            {/* Analytics cookies */}
                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                <div>
                                    <p className="font-medium text-slate-800 dark:text-white">Аналитични бисквитки</p>
                                    <p className="text-xs text-slate-500">Помагат ни да разберем как използвате сайта</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={preferences.analytics}
                                        onChange={(e) => setPreferences(p => ({...p, analytics: e.target.checked}))}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                                </label>
                            </div>
                            
                            {/* Marketing cookies */}
                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                <div>
                                    <p className="font-medium text-slate-800 dark:text-white">Маркетингови бисквитки</p>
                                    <p className="text-xs text-slate-500">Използват се за персонализирана реклама</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={preferences.marketing}
                                        onChange={(e) => setPreferences(p => ({...p, marketing: e.target.checked}))}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                                </label>
                            </div>
                        </div>
                        
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={rejectAll}>
                                Отхвърли всички
                            </Button>
                            <Button size="sm" onClick={acceptSelected} className="bg-amber-500 hover:bg-amber-600 text-white">
                                Запази избора
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CookieConsent;
